import { useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DashboardCard, { EmptyState } from './DashboardCard';
import { chartColors, fmtInt } from './formatters';

const OPTIONS_SHOWN = 3;

/** Activations per ProductName with the leading OptionName values as chips. */
export default function ProductBreakdownCard({ products = [], sx }) {
  const total = products.reduce((s, p) => s + p.count, 0);
  const max = Math.max(1, ...products.map((p) => p.count));

  return (
    <DashboardCard title="Activations by product" subtitle="With the top options (OptionName) inside each product" sx={sx}>
      {products.length === 0 ? (
        <EmptyState />
      ) : (
        <Stack sx={{ gap: 2.25 }}>
          {products.map((p) => (
            <ProductRow key={p.product} product={p} total={total} max={max} />
          ))}
        </Stack>
      )}
    </DashboardCard>
  );
}

function ProductRow({ product: p, total, max }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? p.options : p.options.slice(0, OPTIONS_SHOWN);
  const hidden = p.options.length - OPTIONS_SHOWN;

  return (
    <Stack sx={{ gap: 0.75 }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{p.product}</Typography>
        <Typography variant="body2">
          <Box component="span" sx={{ fontWeight: 500 }}>{fmtInt(p.count)}</Box>
          <Box component="span" sx={{ color: 'text.secondary' }}> · {Math.round((p.count / total) * 100)}%</Box>
        </Typography>
      </Stack>
      <Box sx={{ height: 10, borderRadius: 1.25, bgcolor: 'action.hover', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', borderRadius: 1.25, width: `${(p.count / max) * 100}%`, bgcolor: chartColors.activations }} />
      </Box>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
        {shown.map((o) => (
          <Chip key={o.option || '(none)'} size="small" label={`${o.option || '(no option)'} · ${fmtInt(o.count)}`} />
        ))}
        {hidden > 0 && (
          <Chip
            size="small"
            variant="outlined"
            label={expanded ? 'Show fewer' : `+${hidden} more`}
            onClick={() => setExpanded((v) => !v)}
          />
        )}
      </Stack>
    </Stack>
  );
}
